import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Icon, tokens, Tooltip } from '@hotjar/hotsauce';
import { useState, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LABEL_ICONS } from '../../constants/label-icons';

const containerSize = {
  m: 22,
  l: 28,
} as const;

const containerMaxWidth = {
  m: 34,
  l: 40,
} as const;

const StylesWithText = (size: 'm' | 'l', hasOverflow: boolean) => css`
  width: auto;
  max-width: ${containerMaxWidth[size]}px;
  ${tokens.text('caption')};
  color: ${tokens.color.text_default};
  // smaller label has a smaller font size
  ${size === 'm' && 'font-size: 10px;'}
  ${tokens.truncateText()};
  justify-content: flex-start;

  ${hasOverflow &&
  `
  :after {
    content: '';
    position: absolute;
    bottom: 0;
    right: 0;
    top: 0;
    width: ${tokens.spacer.x2};
    background: linear-gradient(270deg, ${tokens.color.bg_active} 0%, rgba(235, 238, 249, 0) 100%);
  }`};
`;

const Wrapper = styled.span<{ size: 'm' | 'l'; hasText: boolean; hasOverflow: boolean }>`
  position: relative;
  background-color: ${tokens.color.bg_active};
  border-radius: ${({ size }) => (size === 'm' ? tokens.radius.l : '10px')};
  border: ${({ size }) => (size === 'm' ? '1px' : '2px')} solid ${tokens.color.white};
  padding: ${tokens.spacer.x0};

  height: ${({ size }) => containerSize[size]}px;
  width: ${({ size }) => containerSize[size]}px;

  ${tokens.elevation(1)};
  align-items: center;
  display: inline-flex;
  justify-content: center;
  ${({ hasText, hasOverflow, size }) => hasText && StylesWithText(size, hasOverflow)};
`;

interface LabelProps {
  className?: string;
  iconName: React.ComponentProps<typeof Icon>['icon'];
  text: string;
  size: 'm' | 'l';
  keyName: string | null;
}

export function Label({ className, iconName, text, size, keyName }: LabelProps): JSX.Element {
  const { t } = useTranslation();
  const [hasOverflow, setHasOverflow] = useState(false);
  const labelRef = useRef<HTMLSpanElement>(null);

  const displayText = keyName !== null ? t(keyName) : text;

  // Needed to detect if there is an overflow
  useLayoutEffect(() => {
    const { offsetWidth, scrollWidth } = labelRef.current as HTMLSpanElement;
    setHasOverflow(offsetWidth < scrollWidth);
  }, [labelRef, text]);

  return (
    <Tooltip text={displayText}>
      <Wrapper
        ref={labelRef}
        className={className}
        size={size}
        hasText={iconName === 'unknown'}
        hasOverflow={hasOverflow}
        data-testid={`${text} label`}
      >
        {iconName !== 'unknown' ? (
          <Icon icon={DEFAULT_LABEL_ICONS[iconName] ?? iconName} />
        ) : (
          displayText
        )}
      </Wrapper>
    </Tooltip>
  );
}
